import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Factory, Flame, Phone, Mail, MapPin, Settings, Cog, Wrench, Drill, Hammer, Zap, Shield, Network, Upload, Trash2, FileText, Clock } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { COMPANY_INFO, CONTACT_INFO } from '../constants/data';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== "undefined") ? process.env.REACT_APP_BACKEND_URL : "";
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Settings, Cog, Flame, Wrench, Drill, Hammer, Zap, Shield, Network
};

const getIcon = (iconName) => {
  return iconMap[iconName] || Wrench;
};

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  
  const [contactData, setContactData] = useState({
    full_name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    urgency: 'flexible'
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const location = useLocation();

  // URL pre-population
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const descParam = searchParams.get('description');
    const urgencyParam = searchParams.get('urgency');
    
    if (descParam || urgencyParam) {
      setContactData(prev => ({
        ...prev,
        message: descParam ? decodeURIComponent(descParam) : prev.message,
        urgency: urgencyParam ? decodeURIComponent(urgencyParam) : prev.urgency
      }));
    }
  }, [location.search]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = (files) => {
    const allowedExtensions = ['pdf', 'dwg', 'dxf', 'jpg', 'jpeg', 'png', 'webp'];
    const maxSizeBytes = 10485760; // 10MB total
    
    let currentTotalSize = selectedFiles.reduce((acc, f) => acc + f.size, 0);
    const validFiles = [];

    for (let file of files) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        toast.error(`Unsupported file type: ${file.name}. Only PDF, JPG, PNG, WEBP, DWG, DXF are allowed.`);
        continue;
      }
      if (currentTotalSize + file.size > maxSizeBytes) {
        toast.error(`File limit exceeded: Total size must be under 10MB.`);
        break;
      }
      validFiles.push(file);
      currentTotalSize += file.size;
    }
    
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      toast.success(`${validFiles.length} file(s) attached successfully!`);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle smart scroll to target sections after data is loaded
  useEffect(() => {
    if (!isLoading) {
      let targetId = null;
      
      if (location.hash) {
        targetId = location.hash.replace('#', '');
      } else if (location.search) {
        const searchParams = new URLSearchParams(location.search);
        targetId = searchParams.get('section');
      }

      if (targetId) {
        const element = document.getElementById(targetId);
        if (element) {
          // Small timeout to ensure DOM is fully painted
          const timeoutId = setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth' });
          }, 100);
          return () => clearTimeout(timeoutId);
        }
      }
    }
  }, [isLoading, location.hash, location.search]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [productsRes, servicesRes, suppliesRes, settingsRes, contactRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/services`),
        axios.get(`${API}/supplies`),
        axios.get(`${API}/site-settings`),
        axios.get(`${API}/contact-info`)
      ]);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data.slice(0, 6) : []);
      setServices(Array.isArray(servicesRes.data) ? servicesRes.data.slice(0, 6) : []);
      setSupplies(Array.isArray(suppliesRes.data) ? suppliesRes.data : []);
      setSiteSettings(settingsRes.data);
      setContactInfo(contactRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFieldErrors({});
    
    try {
      const formData = new FormData();
      formData.append('full_name', contactData.full_name);
      formData.append('company', contactData.company);
      formData.append('email', contactData.email);
      formData.append('phone', contactData.phone);
      formData.append('message', contactData.message);
      formData.append('urgency', contactData.urgency);
      formData.append('source_page', 'homepage_contact');
      
      selectedFiles.forEach((file) => {
        formData.append('drawings[]', file);
      });

      await axios.post(`${API}/contact-submissions.php`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Your message & quote request has been sent successfully!');
      setContactData({
        full_name: '',
        company: '',
        email: '',
        phone: '',
        message: '',
        urgency: 'flexible'
      });
      setSelectedFiles([]);
    } catch (error) {
      const data = error.response?.data;
      if (data?.field && data?.detail) {
        // Field-specific error: show it inline under the field
        setFieldErrors({ [data.field]: data.detail });
      } else if (error.response?.status === 429) {
        toast.error(data?.detail || 'Too many requests. Please wait a moment.');
      } else {
        toast.error(data?.detail || 'Failed to send message. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0a0a0a]">
      <Toaster position="top-right" theme="dark" />
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={siteSettings?.hero_image_url || "/images/about/main_shop.jpg"}
            alt="Eastend Iron Industries Facility in Taber, Alberta - Professional Steel Fabrication & Welding"
            className="w-full h-full object-cover"
            style={{ opacity: (siteSettings?.hero_image_opacity ?? 40) / 100 }}
          />
          {(siteSettings?.hero_gradient_opacity ?? 0) > 0 && (
            <div 
              className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-transparent to-[#0a0a0a]"
              style={{ opacity: (siteSettings.hero_gradient_opacity ?? 60) / 100 }}
            ></div>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 max-w-5xl mx-auto px-6 text-center"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-block mb-8"
          >
            {siteSettings?.logo_url ? (
              <img
                src={siteSettings.logo_url}
                alt="Eastend Iron Industries Logo - Metal Fabricators in Taber, AB"
                className="mx-auto object-contain"
                style={{
                  width: `${siteSettings.logo_size || 80}px`,
                  height: `${siteSettings.logo_size || 80}px`,
                  filter: (siteSettings.logo_glow_intensity || 0) > 0
                    ? `drop-shadow(0 0 ${siteSettings.logo_glow_intensity}px ${siteSettings.logo_glow_color || '#FF9900'})`
                    : 'none'
                }}
              />
            ) : (
              <Factory className="w-20 h-20 text-[#FF9900] mx-auto" />
            )}
          </motion.div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#F3F4F6] mb-6 text-shadow-glow">
            {siteSettings?.company_name?.split(' ').slice(0, 2).join(' ').toUpperCase() || 'EASTEND IRON'}{' '}
            <span className="text-[#FF9900]">
              {siteSettings?.company_name?.split(' ').slice(2).join(' ').toUpperCase() || 'INDUSTRIES'}
            </span>
          </h1>
          
          <p className="text-xl sm:text-2xl text-gray-300 mb-4 tracking-wide">
            {siteSettings?.tagline || COMPANY_INFO.tagline}
          </p>
          
          <p className="text-base sm:text-lg text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed">
            Steel Fabrication • Welding Services • Custom Manufacturing
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#contact"
              data-testid="hero-contact-button"
              className="inline-flex items-center justify-center gap-2 bg-[#FF9900] text-black px-8 py-4 font-bold uppercase tracking-wider hover:bg-white transition-all duration-300"
            >
              Request A Quote <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="#about"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#FF9900] text-[#FF9900] px-8 py-4 font-bold uppercase tracking-wider hover:bg-[#FF9900] hover:text-black transition-all duration-300"
            >
              Learn More
            </a>
          </div>
        </motion.div>
      </section>


      <section id="about" className="py-20 px-6 bg-[#121212]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.h2
              variants={itemVariants}
              className="text-4xl sm:text-5xl font-bold text-center mb-6 text-[#FF9900]"
            >
              {siteSettings?.about_title?.toUpperCase() || 'ABOUT US'}
            </motion.h2>
            
            <motion.div variants={itemVariants} className="grid md:grid-cols-2 gap-12 mt-12">
              <div>
                <img
                  src={siteSettings?.about_image_url || "/images/about/welder.jpg"}
                  alt="Eastend Iron Industries Shop Floor - Expert Welding and Fabrication Services in Taber"
                  className="w-full h-80 object-cover border-4 border-[#27272A]"
                />
              </div>
              
              <div className="space-y-6">
                <p className="text-gray-300 leading-relaxed text-lg whitespace-pre-line">
                  {siteSettings?.about_content || COMPANY_INFO.about}
                </p>
                
                <div className="grid grid-cols-2 gap-6 mt-8">
                  <div className="bg-[#1E1E1E] p-6 border-l-4 border-[#FF9900]">
                    <div className="text-3xl font-bold text-[#FF9900]">{COMPANY_INFO.founded}</div>
                    <div className="text-sm text-gray-400 mt-1">ESTABLISHED</div>
                  </div>
                  <div className="bg-[#1E1E1E] p-6 border-l-4 border-[#FF9900]">
                    <div className="text-3xl font-bold text-[#FF9900]">20K+</div>
                    <div className="text-sm text-gray-400 mt-1">SQ FT SHOP</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section id="products-preview" className="py-20 px-6 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-bold text-[#FF9900] mb-4">OUR PRODUCTS</h2>
              <p className="text-gray-400 text-lg">Custom-built to your specifications</p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {products.map((product) => {
                const imageUrls = product.image_url ? product.image_url.split(',').map(x => x.trim()).filter(Boolean) : [];
                const firstImg = imageUrls[0] || '/images/placeholder.jpg';
                return (
                  <motion.div
                    key={product.id}
                    variants={itemVariants}
                    data-testid={`product-card-${product.id}`}
                    className="bg-[#121212] border border-[#27272A] hover:border-[#FF9900]/50 transition-all duration-300 overflow-hidden group relative"
                  >
                    <div className="relative h-64 overflow-hidden bg-[#0A0A0A]">
                      <img
                        src={firstImg}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      {imageUrls.length > 1 && (
                        <span className="absolute top-3 right-3 bg-black/80 text-[#FF9900] text-[9px] font-bold px-2 py-0.5 border border-[#FF9900]/30 rounded-full tracking-wider uppercase z-10">
                          {imageUrls.length} Photos
                        </span>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-[#F3F4F6] mb-2 uppercase">{product.name}</h3>
                      <p className="text-gray-400 text-sm mb-3">{product.description}</p>
                      {product.production_time && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-[9px] uppercase font-bold tracking-wider text-gray-500">Production Time:</span>
                          <span className="text-xs font-bold text-[#FF9900] bg-[#FF9900]/10 border border-[#FF9900]/20 px-2 py-0.5 rounded-sm">
                            {product.production_time} {parseInt(product.production_time) === 1 ? 'Day' : 'Days'}
                          </span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            <motion.div variants={itemVariants} className="text-center mt-12">
              <Link
                to="/products"
                data-testid="view-all-products-button"
                className="inline-flex items-center gap-2 bg-[#FF9900] text-black px-8 py-4 font-bold uppercase tracking-wider hover:bg-white transition-all duration-300"
              >
                View All Products <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section id="services-preview" className="py-20 px-6 bg-[#121212]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-bold text-[#FF9900] mb-4">OUR SERVICES</h2>
              <p className="text-gray-400 text-lg">Expert solutions for all your industrial needs</p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {services.map((service) => {
                const Icon = getIcon(service.icon);
                return (
                  <motion.div
                    key={service.id}
                    variants={itemVariants}
                    data-testid={`service-card-${service.id}`}
                    className="bg-[#1E1E1E] border border-[#27272A] p-8 hover:border-[#FF9900] transition-all duration-300 group flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex justify-between items-start mb-4">
                        <Icon className="w-12 h-12 text-[#FF9900] group-hover:scale-110 transition-transform animate-none" />
                        <a 
                          href="#contact"
                          onClick={() => {
                            setContactData(prev => ({ 
                              ...prev, 
                              message: `Inquiry about ${service.name} Service: ` 
                            }));
                          }}
                          className="text-[10px] border border-[#FF9900]/40 text-[#FF9900] hover:bg-[#FF9900] hover:text-black px-3 py-1.5 uppercase font-bold tracking-wider transition-all"
                        >
                          Request Quote
                        </a>
                      </div>
                      <h3 className="text-xl font-bold text-[#F3F4F6] mb-3 uppercase">{service.name}</h3>
                      <p className="text-gray-400 text-sm">{service.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            <motion.div variants={itemVariants} className="text-center mt-12">
              <Link
                to="/services"
                data-testid="view-all-services-button"
                className="inline-flex items-center gap-2 bg-[#FF9900] text-black px-8 py-4 font-bold uppercase tracking-wider hover:bg-white transition-all duration-300"
              >
                View All Services <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section id="supplies" className="py-20 px-6 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-bold text-[#FF9900] mb-4">SUPPLIES</h2>
              <p className="text-gray-400 text-lg">Wide range of metal supplies at competitive prices</p>
            </motion.div>

            <motion.div variants={containerVariants} className="grid sm:grid-cols-2 gap-6">
              {supplies.map((supply, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-[#121212] border-l-4 border-[#FF9900] p-6"
                >
                  <h3 className="text-lg font-bold text-[#F3F4F6] mb-2 uppercase">{supply.name}</h3>
                  <p className="text-gray-400">{supply.description}</p>
                </motion.div>
              ))}
            </motion.div>

            <motion.div variants={itemVariants} className="mt-12 text-center">
              <p className="text-gray-400 text-lg">Call us for pricing and to check availability of stock.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section id="contact" className="py-20 px-6 bg-[#121212]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-bold text-[#FF9900] mb-4">CONTACT US</h2>
              <p className="text-gray-400 text-lg">Get in touch for a quote or consultation</p>
            </motion.div>

            {/* Desktop UI: 4 Columns (Hidden on Mobile) */}
            <motion.div variants={itemVariants} className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {/* Card 1: PHONE */}
              <div className="bg-[#1E1E1E] border border-[#27272A] p-6 hover:border-[#FF9900] transition-colors flex flex-col justify-between">
                <div className="flex items-center gap-3 mb-3">
                  <Phone className="w-5 h-5 text-[#FF9900] shrink-0" />
                  <h3 className="text-md font-bold text-[#F3F4F6] uppercase tracking-wider">Phone</h3>
                </div>
                <p className="text-gray-400 text-sm font-semibold">{contactInfo?.phone || CONTACT_INFO.phone}</p>
              </div>

              {/* Card 2: EMAIL */}
              <div className="bg-[#1E1E1E] border border-[#27272A] p-6 hover:border-[#FF9900] transition-colors flex flex-col justify-between">
                <div className="flex items-center gap-3 mb-3">
                  <Mail className="w-5 h-5 text-[#FF9900] shrink-0" />
                  <h3 className="text-md font-bold text-[#F3F4F6] uppercase tracking-wider">Email</h3>
                </div>
                <p className="text-gray-400 text-sm font-semibold break-all">{contactInfo?.email || CONTACT_INFO.email}</p>
              </div>

              {/* Card 3: ADDRESS */}
              <div className="bg-[#1E1E1E] border border-[#27272A] p-6 hover:border-[#FF9900] transition-colors flex flex-col justify-between">
                <div className="flex items-center gap-3 mb-3">
                  <MapPin className="w-5 h-5 text-[#FF9900] shrink-0" />
                  <h3 className="text-md font-bold text-[#F3F4F6] uppercase tracking-wider">Address</h3>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed font-semibold">
                  {contactInfo?.address || CONTACT_INFO.address}<br />
                  {contactInfo?.city || CONTACT_INFO.city}, {contactInfo?.province || CONTACT_INFO.province} {contactInfo?.postal_code || CONTACT_INFO.postal_code}
                </p>
              </div>

              {/* Card 4: BUSINESS HOURS */}
              <div className="bg-[#1E1E1E] border border-[#27272A] p-6 hover:border-[#FF9900] transition-colors flex flex-col justify-between">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-5 h-5 text-[#FF9900] shrink-0" />
                  <h3 className="text-md font-bold text-[#F3F4F6] uppercase tracking-wider">Hours</h3>
                </div>
                <div className="text-gray-400 text-sm font-semibold space-y-1">
                  {contactInfo?.business_hours
                    ? contactInfo.business_hours.split('\n').map((line, i) => (
                        <div key={i}>{line}</div>
                      ))
                    : <>
                        <div>Mon - Fri: 7:00 AM - 5:00 PM</div>
                        <div>Sat: By Appointment</div>
                        <div>Sun: Closed</div>
                      </>
                  }
                </div>
              </div>
            </motion.div>

            {/* Mobile UI: Grouped 1 Box (Hidden on Desktop) */}
            <motion.div variants={itemVariants} className="block md:hidden bg-[#1E1E1E] border border-[#27272A] p-6 mb-12 space-y-4 divide-y divide-[#27272A]/30">
              {/* Phone item */}
              <div className="flex items-center gap-3 pb-3">
                <Phone className="w-5 h-5 text-[#FF9900] shrink-0" />
                <div>
                  <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Phone</h4>
                  <p className="text-gray-300 text-sm font-semibold">{contactInfo?.phone || CONTACT_INFO.phone}</p>
                </div>
              </div>

              {/* Email item */}
              <div className="flex items-center gap-3 pt-3 pb-3">
                <Mail className="w-5 h-5 text-[#FF9900] shrink-0" />
                <div className="min-w-0">
                  <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Email</h4>
                  <p className="text-gray-300 text-sm font-semibold break-all">{contactInfo?.email || CONTACT_INFO.email}</p>
                </div>
              </div>

              {/* Address item */}
              <div className="flex items-start gap-3 pt-3 pb-3">
                <MapPin className="w-5 h-5 text-[#FF9900] shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Address</h4>
                  <p className="text-gray-300 text-sm font-semibold leading-relaxed">
                    {contactInfo?.address || CONTACT_INFO.address}<br />
                    {contactInfo?.city || CONTACT_INFO.city}, {contactInfo?.province || CONTACT_INFO.province} {contactInfo?.postal_code || CONTACT_INFO.postal_code}
                  </p>
                </div>
              </div>

              {/* Business Hours item */}
              <div className="flex items-start gap-3 pt-3">
                <Clock className="w-5 h-5 text-[#FF9900] shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Business Hours</h4>
                  <div className="text-gray-300 text-sm font-semibold space-y-1 mt-0.5">
                    {contactInfo?.business_hours
                      ? contactInfo.business_hours.split('\n').map((line, i) => (
                          <div key={i}>{line}</div>
                        ))
                      : <>
                          <div>Mon - Fri: 7:00 AM - 5:00 PM</div>
                          <div>Sat: By Appointment</div>
                          <div>Sun: Closed</div>
                        </>
                    }
                  </div>
                </div>
              </div>
            </motion.div>


            {/* Horizontal Contact Form Container */}
            <motion.div variants={itemVariants} className="bg-[#1E1E1E] p-8 border border-[#27272A] max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-[#F3F4F6] mb-6 uppercase text-center border-b border-[#27272A] pb-4">Send Us A Message</h3>
              <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  
                  {/* Row 1: Full Name & Company */}
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={contactData.full_name}
                      onChange={(e) => { setContactData({...contactData, full_name: e.target.value}); setFieldErrors(prev => ({...prev, full_name: ''})); }}
                      data-testid="contact-name-input"
                      className={`w-full bg-[#121212] border ${fieldErrors.full_name ? 'border-red-500' : 'border-[#27272A]'} text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors`}
                    />
                    {fieldErrors.full_name && <p className="text-red-400 text-xs mt-1">{fieldErrors.full_name}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Company</label>
                    <input
                      type="text"
                      value={contactData.company}
                      onChange={(e) => { setContactData({...contactData, company: e.target.value}); setFieldErrors(prev => ({...prev, company: ''})); }}
                      data-testid="contact-company-input"
                      className={`w-full bg-[#121212] border ${fieldErrors.company ? 'border-red-500' : 'border-[#27272A]'} text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors`}
                    />
                    {fieldErrors.company && <p className="text-red-400 text-xs mt-1">{fieldErrors.company}</p>}
                  </div>
                  
                  {/* Row 2: Email & Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      value={contactData.email}
                      onChange={(e) => { setContactData({...contactData, email: e.target.value}); setFieldErrors(prev => ({...prev, email: ''})); }}
                      data-testid="contact-email-input"
                      className={`w-full bg-[#121212] border ${fieldErrors.email ? 'border-red-500' : 'border-[#27272A]'} text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors`}
                    />
                    {fieldErrors.email && <p className="text-red-400 text-xs mt-1">{fieldErrors.email}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Phone *</label>
                    <input
                      type="tel"
                      required
                      pattern="[\d\s\+\-\(\)\.]{8,25}"
                      title="Please enter a valid phone number format (e.g. +1 555 5555, (555) 555-5555, or 12345678)"
                      value={contactData.phone}
                      onChange={(e) => { setContactData({...contactData, phone: e.target.value}); setFieldErrors(prev => ({...prev, phone: ''})); }}
                      data-testid="contact-phone-input"
                      className={`w-full bg-[#121212] border ${fieldErrors.phone ? 'border-red-500' : 'border-[#27272A]'} text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors`}
                    />
                    {fieldErrors.phone && <p className="text-red-400 text-xs mt-1">{fieldErrors.phone}</p>}
                  </div>

                  {/* Row 3: Timeline/Urgency - full-width on desktop to support emergency notifications nicely */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-400 mb-2">Timeline / Urgency *</label>
                    <select
                      required
                      value={contactData.urgency}
                      onChange={(e) => setContactData({...contactData, urgency: e.target.value})}
                      data-testid="contact-urgency-input"
                      className="w-full bg-[#121212] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors"
                    >
                      <option value="flexible">Flexible / General Inquiry</option>
                      <option value="2-4_weeks">2 - 4 weeks</option>
                      <option value="1-2_weeks">1 - 2 weeks</option>
                      <option value="within_this_week">Within this week</option>
                      <option value="asap">As soon as possible</option>
                    </select>
                    
                    {contactData.urgency === 'asap' && (
                      <div className="mt-3 p-3 bg-red-950/40 border border-red-500/50 text-red-200 text-xs flex items-start gap-2 animate-pulse">
                        <Zap className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        <div>
                          <span className="font-bold">CRITICAL SHUTDOWN / EMERGENCY:</span> For immediate estimator dispatch or active emergency repair, please call George directly at <a href="tel:4032232620" className="font-bold underline text-white hover:text-[#FF9900]">(403) 223-2620</a>!
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Row 4: Message - full-width */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-400 mb-2">Message *</label>
                    <textarea
                      required
                      rows={5}
                      value={contactData.message}
                      onChange={(e) => { setContactData({...contactData, message: e.target.value}); setFieldErrors(prev => ({...prev, message: ''})); }}
                      data-testid="contact-message-input"
                      className={`w-full bg-[#121212] border ${fieldErrors.message ? 'border-red-500' : 'border-[#27272A]'} text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors`}
                    ></textarea>
                    {fieldErrors.message && <p className="text-red-400 text-xs mt-1">{fieldErrors.message}</p>}
                  </div>

                  {/* Row 5: Attachment block - full-width */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-400 mb-2">Attach Drawings, Sketched Blueprints, or Images (Optional)</label>
                    <div 
                      onDragEnter={handleDrag}
                      onDragOver={handleDrag}
                      onDragLeave={handleDrag}
                      onDrop={handleDrop}
                      className={`w-full bg-[#121212] border-2 border-dashed ${dragActive ? 'border-[#FF9900] bg-[#FF9900]/5' : 'border-[#27272A]'} p-6 transition-all duration-300 text-center relative group`}
                    >
                      <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.webp"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <Upload className="w-10 h-10 text-gray-500 group-hover:text-[#FF9900] mx-auto mb-3 transition-colors duration-300" />
                      <p className="text-[#F3F4F6] text-sm font-semibold mb-1">
                        Drag and drop files here, or <span className="text-[#FF9900] hover:underline">browse files</span>
                      </p>
                      <p className="text-gray-500 text-xs mt-1">
                        Supports PDF, JPG, PNG, WEBP, DWG, DXF up to 10MB total
                      </p>
                    </div>

                    {selectedFiles.length > 0 && (
                      <div className="mt-4 space-y-2 max-h-[200px] overflow-y-auto custom-scrollbar">
                        {selectedFiles.map((file, index) => (
                          <div 
                            key={index}
                            className="bg-[#121212] border border-[#27272A] p-3 flex items-center justify-between text-xs"
                          >
                            <div className="flex items-center gap-2 overflow-hidden mr-2">
                              <FileText className="w-4 h-4 text-[#FF9900] shrink-0" />
                              <span className="text-gray-300 truncate" title={file.name}>
                                {file.name}
                              </span>
                              <span className="text-gray-500 shrink-0">
                                ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="text-gray-500 hover:text-red-500 p-1 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="w-full bg-[#FF9900] text-black px-8 py-4 font-bold uppercase tracking-wider hover:bg-white transition-all duration-300 disabled:opacity-50"
                >
                  {submitting ? 'Sending Quote Request...' : 'Send Message / Quote Request'}
                </button>
              </form>
              
              <p className="text-xs text-gray-500 mt-4 text-center">
                * Required fields
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
